/**
 * Where a placed order has got to. Mirrors `OrderStatusData::resolveStatus` on
 * the server, which is the only thing that sets it.
 *
 * The two sets below are the whole of the app's interest in it: `live` decides
 * what appears under "happening now" and what keeps polling, `terminal` decides
 * when the polling stops. Both were rebuilt by hand in four separate files
 * before this existed, which is three chances to forget a case.
 */
const ORDER_STATUS = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  READY: 'ready',
  COMPLETED: 'completed',
  REJECTED: 'rejected'
} as const

type OrderStatus = (typeof ORDER_STATUS)[keyof typeof ORDER_STATUS]

/** Plain verbs, sentence case, naming what actually happened. */
const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  pending: 'Waiting to be accepted',
  accepted: 'In the kitchen',
  ready: 'Ready when you are',
  completed: 'Collected',
  rejected: 'Not accepted'
}

/** Still going: worth showing to the diner, and worth asking the server about. */
const isLiveOrderStatus = (status: OrderStatus): boolean =>
  status === ORDER_STATUS.PENDING || status === ORDER_STATUS.ACCEPTED || status === ORDER_STATUS.READY

/** Finished, one way or the other. Nothing after this will change. */
const isTerminalOrderStatus = (status: OrderStatus): boolean =>
  status === ORDER_STATUS.COMPLETED || status === ORDER_STATUS.REJECTED

/** The steps a takeaway shows on its way, in order. */
const ORDER_STEPS = ['Sent', 'In the kitchen', 'Ready'] as const

const ORDER_STEP_READY = 2

/** Each status as an index into `ORDER_STEPS`. Collected is past the last step, so every bar is filled. */
const ORDER_STEP_BY_STATUS: Partial<Record<OrderStatus, number>> = {
  [ORDER_STATUS.PENDING]: 0,
  [ORDER_STATUS.ACCEPTED]: 1,
  [ORDER_STATUS.READY]: ORDER_STEP_READY,
  [ORDER_STATUS.COMPLETED]: ORDER_STEPS.length
}

const orderStepFor = (status: OrderStatus): number => ORDER_STEP_BY_STATUS[status] ?? 0

export {
  isLiveOrderStatus,
  isTerminalOrderStatus,
  ORDER_STATUS,
  ORDER_STATUS_LABEL,
  ORDER_STEP_BY_STATUS,
  ORDER_STEP_READY,
  ORDER_STEPS,
  orderStepFor
}
export type { OrderStatus }
